// @ts-strict-ignore
import * as React from 'react';
import { AppComponentProps } from '@/typescriptHelpers';
import Descriptions from 'antd/lib/descriptions';
import Text from 'antd/lib/typography/Text';
import { truncate } from 'lodash';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { groupReleaseTypesByValue, releaseTypeTabTitle } from '../ReleaseTypeTabs';
import {
    selectAddFixRequestsToOpenPRsAgainstReleaseBranches,
    selectAppSettingAutomations,
    selectAppSettings,
    selectEnableStatusChecksForFixRequests,
    selectFixRequestsAutoMergeUserCreatedPRsSettings,
    selectPreventFixRequestsAfterSubmission,
    selectPreventFixRequestsAfterSubmissionCustomCopyEnabled,
    selectPreventFixRequestsAfterSubmissionCustomCopy,
    selectRequiredFixRequestsApprovals,
    selectRequiresFixRequestsApprovals,
    selectThreadFixNotifications,
    selectVCSSupportsStatusChecks,
    selectFixRequestFormFieldsSettings,
    selectFixApprovalMode,
    selectFixApprovalWindowTrigger,
    selectFixApprovalRequireForPreWindowFixes,
    selectFixApprovalRequiredApproverUserGroupIds,
    selectFixApprovalRequiredApproverUserIds,
    selectFixApprovalDisallowCodeAuthor,
    selectFixApprovalImplicitCreatorApproval,
    selectOrgSettings,
    selectOrgUsers,
    selectStepConfigDataFromStepGraphs,
    selectResetFixApprovalOnNewCommit,
} from '../../selectors/selectors';
import {
    AutoCherryPickMatchTokenOn,
    AutoTriggerCIWorkflowData,
    FixesTokenSettings,
    AutomationType,
    IntegrationType,
    MergeType,
    PullRequestType,
    RelativeTiming,
    TriggerCIWorkflowConditionGroupType,
} from '../../types';
import { displayLifecycleEventName } from '../../helpers/displayHelpers';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import EditOpenPRFixesModal, { fixesTokenSettingsForReleaseType } from './EditOpenPRFixesModal';
import EditFixApprovalsModal from './EditFixApprovalsModal';
import EditFixFormTemplateModal from './EditFixFormTemplateModal';
import EditAutoMergeFixesModal from './EditAutoMergeFixesModal';
import AutomationSettingsBanner from '../sharedComponents/AutomationSettingsBanner';
import { Flex, Space, Tooltip } from 'antd/lib';
import { runwayColors } from '../styles/globalStyles';
import { getFixRequestFormFieldId } from '@/helpers';

const FIX_PULL_REQUEST_TYPES = [
    PullRequestType.CherryPickWorkingBranch,
    PullRequestType.HotfixCherryPick,
    PullRequestType.ManualConflictResolution,
];

const FixRequestsSettings: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, props.appId));

    const autoCherryPickAutomationId = useSelector((state: RootState) => {
        const automations = selectAppSettingAutomations(state, props.appId);
        const autoCherryPickAutomation = automations.find(
            (automation) => automation.type === AutomationType.AutoCherryPickFromWorkingBranch,
        );
        return autoCherryPickAutomation?.id;
    });

    const addFixRequestsToOpenPRsAgainstReleaseBranches = useSelector(
        selectAddFixRequestsToOpenPRsAgainstReleaseBranches(props.appId),
    );
    const requiresFixRequestsApprovals = useSelector(selectRequiresFixRequestsApprovals(props.appId));
    const requiredFixRequestsApprovals = useSelector(selectRequiredFixRequestsApprovals(props.appId));
    const fixApprovalMode = useSelector(selectFixApprovalMode(props.appId));
    const fixApprovalWindowTrigger = useSelector(selectFixApprovalWindowTrigger(props.appId));
    const fixApprovalRequireForPreWindowFixes = useSelector(selectFixApprovalRequireForPreWindowFixes(props.appId));
    const fixApprovalRequiredApproverUserGroupIds = useSelector(
        selectFixApprovalRequiredApproverUserGroupIds(props.appId),
    );
    const fixApprovalRequiredApproverUserIds = useSelector(selectFixApprovalRequiredApproverUserIds(props.appId));
    const fixApprovalDisallowCodeAuthor = useSelector(selectFixApprovalDisallowCodeAuthor(props.appId));
    const fixApprovalImplicitCreatorApproval = useSelector(selectFixApprovalImplicitCreatorApproval(props.appId));
    const orgSettings = useSelector((state: RootState) => selectOrgSettings(state, props.orgId));
    const orgGroups = orgSettings?.groups ?? {};
    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, props.orgId));
    const vcsSupportsStatusChecks = useSelector(selectVCSSupportsStatusChecks(props.appId, props.orgId));
    const enableStatusChecksForFixRequests = useSelector(selectEnableStatusChecksForFixRequests(props.appId));
    const resetFixApprovalOnNewCommit = useSelector(selectResetFixApprovalOnNewCommit(props.appId));
    const fixRequestFormFieldsSettings = useSelector(selectFixRequestFormFieldsSettings(props.appId));
    const threadRelatedFixNotifications = useSelector(selectThreadFixNotifications(props.appId));
    const preventFixRequestsAfterSubmissionCustomCopy = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopy(props.appId),
    );
    const preventFixRequestsAfterSubmissionCustomCopyEnabled = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopyEnabled(props.appId),
    );
    const preventFixRequestsAfterSubmission = useSelector(selectPreventFixRequestsAfterSubmission(props.appId));
    const { mergeUserCreatedPRs, workingBranchUserCreatedPRMergeType, releaseBranchUserCreatedPRMergeType } =
        useSelector(selectFixRequestsAutoMergeUserCreatedPRsSettings(props.appId));

    const pullFixesIntoTheReleaseAutomation = useSelector((state: RootState) => {
        const automations = selectAppSettingAutomations(state, props.appId);
        return automations.find((automation) => automation.type === AutomationType.AutoCherryPickFromWorkingBranch);
    });

    const isPullFixesIntoTheReleaseAutomationEnabled = pullFixesIntoTheReleaseAutomation?.enabled ?? false;
    const fixesTokenGroups = groupReleaseTypesByValue((releaseType) =>
        fixesTokenSettingsForReleaseType(appSettings.fixesTokenConfig, releaseType),
    );
    const enabledFixesTokenGroups = fixesTokenGroups.filter(({ value }) => value.enabled);
    const renderFixesTokenValue = (
        groups: typeof fixesTokenGroups,
        display: (settings: FixesTokenSettings) => string,
    ): React.ReactNode => {
        if (fixesTokenGroups.length === 1) {
            return display(groups[0].value);
        }

        return (
            <Flex vertical>
                {groups.map(({ releaseTypes, value }) => (
                    <Text key={releaseTypes.join(',')}>
                        {'\u2022'} {releaseTypeTabTitle(releaseTypes)}: {display(value)}
                    </Text>
                ))}
            </Flex>
        );
    };

    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, props.appId));
    const stepGraphStepsMap = useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, props.appId));
    const triggerCIWorkflowAutomations = React.useMemo(
        () => (automations ?? []).filter((automation) => automation.type === AutomationType.AutoTriggerCIWorkflow),
        [automations],
    );

    const hasSlackIntegration = appSettings.integrations.some(
        (integration) => integration.id === IntegrationType.Slack,
    );

    // Each fix setting group is edited in its own modal; this tracks which one is open (if any).
    type OpenModal = 'openPRs' | 'approvals' | 'formTemplate' | 'autoMerge' | null;
    const [openModal, setOpenModal] = React.useState<OpenModal>(null);

    const renderEditButton = (modal: Exclude<OpenModal, null>) => (
        <PermissionedActionButton
            actionType={RunwayUserAction.UAUpdateAppSettings}
            type="link"
            icon={<EditOutlined />}
            onClick={() => setOpenModal(modal)}
        >
            Edit
        </PermissionedActionButton>
    );

    const displayMergeStrategyValue = (strategy: MergeType) => {
        switch (strategy) {
            case MergeType.DoNotMerge:
                return 'Do not merge user-created PRs';
            case MergeType.MergeCommit:
                return 'Merge user-created PRs with merge commit';
            case MergeType.Squash:
                return 'Squash merge user-created PRs';
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Descriptions
                title="Fix creation"
                size="middle"
                layout="horizontal"
                bordered
                column={1}
                labelStyle={{ width: 300 }}
                extra={renderEditButton('openPRs')}
            >
                <Descriptions.Item label="Create a fix for open PRs against the release branch">
                    {addFixRequestsToOpenPRsAgainstReleaseBranches ? 'Yes' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Allow PRs to be flagged as fixes using a token">
                    {renderFixesTokenValue(fixesTokenGroups, (settings) => (settings.enabled ? 'Yes' : 'No'))}
                </Descriptions.Item>
                {enabledFixesTokenGroups.length > 0 && (
                    <>
                        <Descriptions.Item label="Fix token patterns">
                            {renderFixesTokenValue(enabledFixesTokenGroups, (settings) =>
                                settings.fixRequestTokens.join(', '),
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Match token on">
                            {renderFixesTokenValue(enabledFixesTokenGroups, (settings) =>
                                settings.matchTokenOn
                                    .map((tokenOn) =>
                                        tokenOn === AutoCherryPickMatchTokenOn.Label ? 'PR label' : 'PR title',
                                    )
                                    .join(', '),
                            )}
                        </Descriptions.Item>
                    </>
                )}
                <Descriptions.Item label="Disallow new fix requests once release is submitted">
                    <Flex vertical>
                        <Text>{preventFixRequestsAfterSubmission ? 'Yes' : 'No'}</Text>
                        {preventFixRequestsAfterSubmissionCustomCopyEnabled && preventFixRequestsAfterSubmission ?
                            <Text>
                                <b>Additional copy:</b>{' '}
                                {truncate(preventFixRequestsAfterSubmissionCustomCopy, { length: 50 })}
                            </Text>
                        :   null}
                    </Flex>
                </Descriptions.Item>
                {hasSlackIntegration && (
                    <Descriptions.Item label="Thread related fix notifications">
                        {threadRelatedFixNotifications ? 'Yes' : 'No'}
                    </Descriptions.Item>
                )}
            </Descriptions>

            <Descriptions
                title="Fix approvals"
                size="middle"
                layout="horizontal"
                bordered
                column={1}
                labelStyle={{ width: 300 }}
                extra={renderEditButton('approvals')}
            >
                <Descriptions.Item label="Fix approvals enabled">
                    {requiresFixRequestsApprovals ? 'Yes' : 'No'}
                </Descriptions.Item>
                {requiresFixRequestsApprovals && (
                    <Descriptions.Item label="Approval mode">
                        {fixApprovalMode === 'window' ?
                            'Require fix approvals during specific windows only'
                        :   'Always require fix approvals'}
                    </Descriptions.Item>
                )}
                {requiresFixRequestsApprovals && fixApprovalMode === 'window' && fixApprovalWindowTrigger && (
                    <Descriptions.Item label="Approval window">
                        {(() => {
                            const parts: string[] = [];
                            if (fixApprovalWindowTrigger.relativeDays)
                                parts.push(`${fixApprovalWindowTrigger.relativeDays}d`);
                            if (fixApprovalWindowTrigger.relativeHours)
                                parts.push(`${fixApprovalWindowTrigger.relativeHours}h`);
                            if (fixApprovalWindowTrigger.relativeMinutes)
                                parts.push(`${fixApprovalWindowTrigger.relativeMinutes}m`);
                            const timeStr = parts.length > 0 ? parts.join(' ') : '0h';
                            const timing =
                                fixApprovalWindowTrigger.timing === RelativeTiming.before ? 'before'
                                : fixApprovalWindowTrigger.timing === RelativeTiming.onTime ? 'at'
                                : 'after';
                            const event =
                                fixApprovalWindowTrigger.lifecycleEventType ?
                                    displayLifecycleEventName(fixApprovalWindowTrigger.lifecycleEventType)
                                :   '';
                            return `${timeStr} ${timing} ${event}`.trim();
                        })()}
                    </Descriptions.Item>
                )}
                {requiresFixRequestsApprovals && fixApprovalMode === 'window' && (
                    <Descriptions.Item label="For fix requests that are">
                        {fixApprovalRequireForPreWindowFixes ? 'Created, or unmerged' : 'Created'}
                    </Descriptions.Item>
                )}
                {requiresFixRequestsApprovals && (
                    <Descriptions.Item label="Number of approvals required">
                        {requiredFixRequestsApprovals}
                    </Descriptions.Item>
                )}
                {requiresFixRequestsApprovals &&
                    (fixApprovalRequiredApproverUserGroupIds.length > 0 ||
                        fixApprovalRequiredApproverUserIds.length > 0) && (
                        <Descriptions.Item label="Required approvers">
                            {[
                                ...fixApprovalRequiredApproverUserGroupIds.map((id) => orgGroups[id]?.name ?? id),
                                ...fixApprovalRequiredApproverUserIds.map((id) => {
                                    const user = orgUsers.find((u) => u.id === id);
                                    return (
                                        [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || id
                                    );
                                }),
                            ].join(', ')}
                        </Descriptions.Item>
                    )}
                {requiresFixRequestsApprovals &&
                    (fixApprovalRequiredApproverUserGroupIds.length > 0 ||
                        fixApprovalRequiredApproverUserIds.length > 0) && (
                        <Descriptions.Item label="Implicit approval from required approver creators">
                            {fixApprovalImplicitCreatorApproval ? 'Yes' : 'No'}
                        </Descriptions.Item>
                    )}
                {requiresFixRequestsApprovals && (
                    <Descriptions.Item label="Prevent approval from code authors">
                        {fixApprovalDisallowCodeAuthor ? 'Yes' : 'No'}
                    </Descriptions.Item>
                )}
                {vcsSupportsStatusChecks && (
                    <Descriptions.Item label="Status checks enabled">
                        {requiresFixRequestsApprovals && enableStatusChecksForFixRequests ? 'Yes' : 'No'}
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Reset fix approvals when a new commit is pushed">
                    {requiresFixRequestsApprovals && resetFixApprovalOnNewCommit ? 'Yes' : 'No'}
                </Descriptions.Item>
            </Descriptions>

            <Descriptions
                title="Fix request form template"
                size="middle"
                layout="horizontal"
                bordered
                column={1}
                labelStyle={{ width: 300 }}
                extra={renderEditButton('formTemplate')}
            >
                <Descriptions.Item label="Fix request form fields">
                    {fixRequestFormFieldsSettings.length > 0 ?
                        (() => {
                            const MAX_VISIBLE = 3;
                            const visible = fixRequestFormFieldsSettings.slice(0, MAX_VISIBLE);
                            const rest = fixRequestFormFieldsSettings.length - MAX_VISIBLE;
                            return (
                                <Space size={4} wrap>
                                    {visible.map((f, idx) => (
                                        <span key={f.name}>
                                            {f.name}
                                            <Tooltip
                                                title={
                                                    <Space size={4}>
                                                        <Text style={{ color: 'inherit' }}>Field ID:</Text>
                                                        <Text code style={{ color: 'inherit' }}>
                                                            {getFixRequestFormFieldId(f)}
                                                        </Text>
                                                    </Space>
                                                }
                                            >
                                                <InfoCircleOutlined
                                                    style={{ color: runwayColors.darkTextGrey, marginLeft: 4 }}
                                                />
                                            </Tooltip>
                                            {idx < visible.length - 1 || rest > 0 ? ',' : ''}
                                        </span>
                                    ))}
                                    {rest > 0 ?
                                        <Text>and {rest} more</Text>
                                    :   null}
                                </Space>
                            );
                        })()
                    :   'None'}
                </Descriptions.Item>
            </Descriptions>

            <Descriptions
                title="Auto-merge fixes"
                size="middle"
                layout="horizontal"
                bordered
                column={1}
                labelStyle={{ width: 300 }}
                extra={renderEditButton('autoMerge')}
            >
                <Descriptions.Item label="Auto-merge user-created PRs with fixes enabled">
                    {mergeUserCreatedPRs ? 'Yes' : 'No'}
                </Descriptions.Item>
                {mergeUserCreatedPRs && (
                    <Descriptions.Item label="Working branch auto-merge strategy">
                        {displayMergeStrategyValue(workingBranchUserCreatedPRMergeType)}
                    </Descriptions.Item>
                )}
                {mergeUserCreatedPRs && (
                    <Descriptions.Item label="Release branch auto-merge strategy">
                        {displayMergeStrategyValue(releaseBranchUserCreatedPRMergeType)}
                    </Descriptions.Item>
                )}
            </Descriptions>

            <div>
                <AutomationSettingsBanner
                    appId={props.appId}
                    orgId={props.orgId}
                    isEnabled={isPullFixesIntoTheReleaseAutomationEnabled}
                    automationId={autoCherryPickAutomationId}
                    bannerText={
                        isPullFixesIntoTheReleaseAutomationEnabled ?
                            <Text style={{ flexGrow: 1, marginRight: 15 }}>
                                &quot;Pull (cherry-pick) fixes into the release&quot; (automation) is <b>enabled</b>.
                                You can disable or manage options from its settings.
                            </Text>
                        :   <Text type="secondary" style={{ flexGrow: 1, marginRight: 15 }}>
                                &quot;Pull (cherry-pick) fixes into the release&quot; (automation) is <b>disabled</b>.
                                Any approved fixes will need to be manually cherry-picked and merged. You can enable and
                                manage options from the automation’s settings.
                            </Text>
                    }
                />
            </div>

            {appSettings.useTriggerCIWorkflowConditionGroups &&
                triggerCIWorkflowAutomations.map((automation) => {
                    const conditionGroups =
                        (automation.data as AutoTriggerCIWorkflowData | undefined)?.conditionGroups ?? [];
                    const triggersOnFixPRs = conditionGroups.some(
                        (group) =>
                            group.type === TriggerCIWorkflowConditionGroupType.PullRequest &&
                            (group.runwayPRTypes ?? []).some((prType) => FIX_PULL_REQUEST_TYPES.includes(prType)),
                    );
                    const isEnabled = automation.enabled && triggersOnFixPRs;
                    const stepName = stepGraphStepsMap[automation.stepConfigId]?.stepName ?? 'CI workflow';
                    return (
                        <div key={automation.id}>
                            <AutomationSettingsBanner
                                appId={props.appId}
                                orgId={props.orgId}
                                isEnabled={isEnabled}
                                automationId={automation.id}
                                bannerText={
                                    isEnabled ?
                                        <Text style={{ flexGrow: 1, marginRight: 15 }}>
                                            &quot;Trigger CI workflow&quot; (automation) on step <b>{stepName}</b> is{' '}
                                            <b>enabled</b> for fix-related pull requests. You can disable or manage
                                            options from its settings.
                                        </Text>
                                    :   <Text type="secondary" style={{ flexGrow: 1, marginRight: 15 }}>
                                            &quot;Trigger CI workflow&quot; (automation) on step <b>{stepName}</b> is
                                            not triggering builds for fix-related pull requests. You can enable and
                                            manage options from the automation’s settings.
                                        </Text>
                                }
                            />
                        </div>
                    );
                })}

            <EditOpenPRFixesModal
                open={openModal === 'openPRs'}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => setOpenModal(null)}
            />
            <EditFixApprovalsModal
                open={openModal === 'approvals'}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => setOpenModal(null)}
            />
            <EditFixFormTemplateModal
                open={openModal === 'formTemplate'}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => setOpenModal(null)}
            />
            <EditAutoMergeFixesModal
                open={openModal === 'autoMerge'}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => setOpenModal(null)}
            />
        </div>
    );
};

export default FixRequestsSettings;
